import React, { useState, useEffect } from 'react';
import Observer from '@researchgate/react-intersection-observer';

import { Spin, Modal } from 'antd';

import ReactECharts from 'echarts-for-react';
import moment from 'moment';
import { Point, Simplify } from 'curvereduce';

import * as ToolRequest from '../../../tools/ToolRequest';

import StationDashboardGraphDownloadModal from './StationDashboardGraphDownloadModal';

export default function StationDashboardGraphInputChart(props) {
  const {
    zoneId,
    zoneIoId,
    ioName,
    settings,
    beginAt,
    endAt,
    selectedRange,
    suffix,
    min,
    max,
    interval,
    formatter = v => v,
    labelFormatter = v => v,
    showMarker = true,
    color = '#2C5BFF',
  } = props;

  const [chartData, setChartData] = useState({
    prev: [],
    base: [],
    baseSimplified: [],
    next: [],
  });
  const [markAreaData, setMarkAreaData] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [graphModalKey, setGraphModalKey] = useState(null);
  const [graphModalVisible, setGraphModalVisible] = useState(false);
  const [downloadModalKey, setDownloadModalKey] = useState(null);
  const [downloadModalVisible, setDownloadModalVisible] = useState(false);

  useEffect(() => {
    if (loaded) {
      (async () => {
        const sensorRecordsRes = await ToolRequest.request('GET', '/v1/station-sensor-record-quick', {
          ioName,
          zoneId,
          beginAt: beginAt.toISOString(),
          endAt: endAt.toISOString(),
        });

        const sensorRecordBoundaryRes = await ToolRequest.request('GET', '/v1/station-sensor-record-quick-boundary', {
          ioName,
          zoneId,
          beginAt: beginAt.toISOString(),
          endAt: endAt.toISOString(),
        });

        const rangeDiff = endAt.valueOf() - beginAt.valueOf() - 1;

        const chartDataNew = {
          prev: [],
          base: sensorRecordsRes.data,
          baseSimplified: [],
          next: [],
        };

        // prev & next
        if (sensorRecordsRes.data.length) {
          // prev
          if (sensorRecordBoundaryRes.prevValue !== null) {
            const prevTimeRange = beginAt.valueOf() - moment(sensorRecordBoundaryRes.prevCreatedAt).valueOf() + sensorRecordsRes.data[0].timeOffset;
            const valueProportional = (sensorRecordBoundaryRes.prevValue - sensorRecordsRes.data[0].value) * sensorRecordsRes.data[0].timeOffset / prevTimeRange + sensorRecordsRes.data[0].value;

            chartDataNew.prev.push({
              timeOffset: 0,
              value: valueProportional,
            });
            chartDataNew.prev.push({
              timeOffset: sensorRecordsRes.data[0].timeOffset,
              value: sensorRecordBoundaryRes.prevValue,
            });
          } else {
            chartDataNew.prev.push({
              timeOffset: 0,
              value: 0,
            });
            chartDataNew.prev.push({
              timeOffset: sensorRecordsRes.data[0].timeOffset,
              value: 0,
            });
          }

          // next
          if (sensorRecordBoundaryRes.nextValue !== null) {
            const nextTimeRange = moment(sensorRecordBoundaryRes.nextCreatedAt).valueOf() - (beginAt.valueOf() + sensorRecordsRes.data[sensorRecordsRes.data.length - 1].timeOffset);
            const valueProportional = (sensorRecordBoundaryRes.nextValue - sensorRecordsRes.data[sensorRecordsRes.data.length - 1].value) * (rangeDiff - sensorRecordsRes.data[sensorRecordsRes.data.length - 1].timeOffset) / nextTimeRange + sensorRecordsRes.data[sensorRecordsRes.data.length - 1].value;

            chartDataNew.next.push({
              timeOffset: sensorRecordsRes.data[sensorRecordsRes.data.length - 1].timeOffset,
              value: valueProportional,
            });
            chartDataNew.next.push({
              timeOffset: rangeDiff,
              value: sensorRecordBoundaryRes.nextValue,
            });
          } else {
            chartDataNew.next.push({
              timeOffset: sensorRecordsRes.data[sensorRecordsRes.data.length - 1].timeOffset,
              value: sensorRecordsRes.data[sensorRecordsRes.data.length - 1].value,
            });
            chartDataNew.next.push({
              timeOffset: rangeDiff,
              value: sensorRecordsRes.data[sensorRecordsRes.data.length - 1].value,
            });
          }
        } else {
          // only prev
          if (sensorRecordBoundaryRes.prevValue !== null) {
            chartDataNew.prev.push({
              timeOffset: 0,
              value: sensorRecordBoundaryRes.prevValue,
            });
            chartDataNew.prev.push({
              timeOffset: rangeDiff,
              value: sensorRecordBoundaryRes.prevValue,
            });
          }
        }

        if (sensorRecordsRes.data.length) {
          // simplify graph
          const points = chartDataNew.base.map(record => ({
            x: record.timeOffset,
            y: record.value,
          }));
          const simplified = Simplify(points, 0.4);
          chartDataNew.baseSimplified = simplified.map(point => {
            return {
              timeOffset: point.x,
              value: point.y,
            };
          });

          // calculate markArea
          const markAreaData = [];

          let markAreaActive = false;
          let markAreaBeginAt = null;

          const targetValueRange = (max - min) * settings.targetValueSafePercent / 2 * 1.5;
          const targetValueLower = settings.targetValue - targetValueRange;
          const targetValueUpper = settings.targetValue + targetValueRange;

          for (let i = 0; i < chartData.length; ++i) {
            if (markAreaActive) {
              if (chartData[i][1] >= targetValueLower && chartData[i][1] <= targetValueUpper) {
                markAreaData.push([
                  {
                    xAxis: markAreaBeginAt,
                  },
                  {
                    xAxis: chartData[i][0],
                  },
                ]);
                markAreaActive = false;
              }
            } else {
               if (chartData[i][1] < targetValueLower || chartData[i][1] > targetValueUpper) {
                 markAreaBeginAt = chartData[i][0];
                 markAreaActive = true;
               }
            }
          }

          if (markAreaActive) {
            markAreaData.push([
              {
                xAxis: markAreaBeginAt,
              },
              {
                xAxis: endAt.format('YYYY-MM-DD HH:mm:ss'),
              },
            ]);
          }
        }

        const convertSeries = (series) => {
          return series.map(record => {
            return [
              moment(beginAt).add(record.timeOffset).format('YYYY-MM-DD HH:mm:ss'),
              record.value,
            ];
          })
        };

        setChartData({
          prev: convertSeries(chartDataNew.prev),
          base: convertSeries(chartDataNew.base),
          baseSimplified: convertSeries(chartDataNew.baseSimplified),
          next: convertSeries(chartDataNew.next),
        });

        setMarkAreaData(markAreaData);

        setLoading(false);
      })();
    }
  }, [loaded]);

  const option = {
    title: {
      text: ioName,
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      containLabel: true,
    },
    xAxis: {
      type: 'time',
      boundaryGap: false,
    },
    yAxis: {
      type: 'value',
      interval,
      min,
      max,
      axisLabel: {
        formatter: labelFormatter,
      },
    },
    toolbox: {
      feature: {
        dataZoom: {
          yAxisIndex: 'none'
        },
      }
    },
    dataZoom: [{
      type: 'inside',
    }],
    tooltip: {
      trigger: 'axis',
      formatter: (params) => {
        let dataArrs = [];
        if (params.some(param => param.seriesIndex === 1)) {
          dataArrs.push(`<div>${params[0].marker}<b>${formatter(params[0].value[1])}${suffix || ''}</b></div>`);
        }
        return `${moment(params[0].value[0]).format('YYYY-MM-DD HH:mm:ss')}` + dataArrs.join('');
      },
      axisPointer: {
        animation: false
      },
    },
    series: [
      {
        type: 'line',
        symbolSize: 0,
        data: [],   // override below
        name: ioName,
        lineStyle: {
          color,
          type: 'solid',
          opacity: .4,
        },
        itemStyle: {
          color,
        },
      },
      {
        type: 'line',
        symbolSize: 0,
        data: [],   // override below
        lineStyle: {
          color,
          type: 'solid',
        },
        itemStyle: {
          color,
        },
        ...(markAreaData.length ? {
          markArea: {
            itemStyle: {
              color: 'rgba(255, 173, 177, 0.4)',
            },
            data: markAreaData,
          },
        } : {}),
        ...(showMarker ? {
          markPoint: {
            data: [
              { type: 'max', name: 'Highest' },
              { type: 'min', name: 'Lowest' },
            ],
            label: {
              formatter: (param) => {
                return formatter(param.data.value);
              },
            },
          },
          markLine: {
            data: [
              { type: 'average', name: 'Average' },
            ],
            label: {
              position: 'insideEndTop',
              formatter: (param) => {
                return formatter(param.data.value);
              },
            },
          },
        } : {}),
      },
      {
        type: 'line',
        symbolSize: 0,
        data: [],   // override below
        name: ioName,
        lineStyle: {
          color,
          type: 'solid',
          opacity: .4,
        },
        itemStyle: {
          color,
        },
      },
    ],
  };

  return (
    <Spin spinning={loading}>
      <Observer
        onChange={(event) => {
          if (event.isIntersecting) {
            setLoaded(true);
          }
        }}
      >
        <ReactECharts
          {...props}
          opts={{
            useDirtyRect: true,
          }}
          option={{
            ...option,
            toolbox: {
              feature: {
                myExpand: {
                  show: true,
                  title: 'Expand',
                  icon: 'path://M4.75 9.233C4.75 9.64721 5.08579 9.983 5.5 9.983C5.91421 9.983 6.25 9.64721 6.25 9.233H4.75ZM6.25 5.5C6.25 5.08579 5.91421 4.75 5.5 4.75C5.08579 4.75 4.75 5.08579 4.75 5.5H6.25ZM5.5 4.75C5.08579 4.75 4.75 5.08579 4.75 5.5C4.75 5.91421 5.08579 6.25 5.5 6.25V4.75ZM9.233 6.25C9.64721 6.25 9.983 5.91421 9.983 5.5C9.983 5.08579 9.64721 4.75 9.233 4.75V6.25ZM6.03033 4.96967C5.73744 4.67678 5.26256 4.67678 4.96967 4.96967C4.67678 5.26256 4.67678 5.73744 4.96967 6.03033L6.03033 4.96967ZM9.96967 11.0303C10.2626 11.3232 10.7374 11.3232 11.0303 11.0303C11.3232 10.7374 11.3232 10.2626 11.0303 9.96967L9.96967 11.0303ZM15.767 18.75C15.3528 18.75 15.017 19.0858 15.017 19.5C15.017 19.9142 15.3528 20.25 15.767 20.25V18.75ZM19.5 20.25C19.9142 20.25 20.25 19.9142 20.25 19.5C20.25 19.0858 19.9142 18.75 19.5 18.75V20.25ZM18.75 19.5C18.75 19.9142 19.0858 20.25 19.5 20.25C19.9142 20.25 20.25 19.9142 20.25 19.5H18.75ZM20.25 15.767C20.25 15.3528 19.9142 15.017 19.5 15.017C19.0858 15.017 18.75 15.3528 18.75 15.767H20.25ZM18.9697 20.0303C19.2626 20.3232 19.7374 20.3232 20.0303 20.0303C20.3232 19.7374 20.3232 19.2626 20.0303 18.9697L18.9697 20.0303ZM15.0303 13.9697C14.7374 13.6768 14.2626 13.6768 13.9697 13.9697C13.6768 14.2626 13.6768 14.7374 13.9697 15.0303L15.0303 13.9697ZM6.25 15.767C6.25 15.3528 5.91421 15.017 5.5 15.017C5.08579 15.017 4.75 15.3528 4.75 15.767H6.25ZM4.75 19.5C4.75 19.9142 5.08579 20.25 5.5 20.25C5.91421 20.25 6.25 19.9142 6.25 19.5H4.75ZM5.5 18.75C5.08579 18.75 4.75 19.0858 4.75 19.5C4.75 19.9142 5.08579 20.25 5.5 20.25V18.75ZM9.233 20.25C9.64721 20.25 9.983 19.9142 9.983 19.5C9.983 19.0858 9.64721 18.75 9.233 18.75V20.25ZM4.96967 18.9697C4.67678 19.2626 4.67678 19.7374 4.96967 20.0303C5.26256 20.3232 5.73744 20.3232 6.03033 20.0303L4.96967 18.9697ZM11.0303 15.0303C11.3232 14.7374 11.3232 14.2626 11.0303 13.9697C10.7374 13.6768 10.2626 13.6768 9.96967 13.9697L11.0303 15.0303ZM15.767 4.75C15.3528 4.75 15.017 5.08579 15.017 5.5C15.017 5.91421 15.3528 6.25 15.767 6.25V4.75ZM19.5 6.25C19.9142 6.25 20.25 5.91421 20.25 5.5C20.25 5.08579 19.9142 4.75 19.5 4.75V6.25ZM20.25 5.5C20.25 5.08579 19.9142 4.75 19.5 4.75C19.0858 4.75 18.75 5.08579 18.75 5.5H20.25ZM18.75 9.233C18.75 9.64721 19.0858 9.983 19.5 9.983C19.9142 9.983 20.25 9.64721 20.25 9.233H18.75ZM20.0303 6.03033C20.3232 5.73744 20.3232 5.26256 20.0303 4.96967C19.7374 4.67678 19.2626 4.67678 18.9697 4.96967L20.0303 6.03033ZM13.9697 9.96967C13.6768 10.2626 13.6768 10.7374 13.9697 11.0303C14.2626 11.3232 14.7374 11.3232 15.0303 11.0303L13.9697 9.96967ZM6.25 9.233V5.5H4.75V9.233H6.25ZM5.5 6.25H9.233V4.75H5.5V6.25ZM4.96967 6.03033L9.96967 11.0303L11.0303 9.96967L6.03033 4.96967L4.96967 6.03033ZM15.767 20.25H19.5V18.75H15.767V20.25ZM20.25 19.5V15.767H18.75V19.5H20.25ZM20.0303 18.9697L15.0303 13.9697L13.9697 15.0303L18.9697 20.0303L20.0303 18.9697ZM4.75 15.767V19.5H6.25V15.767H4.75ZM5.5 20.25H9.233V18.75H5.5V20.25ZM6.03033 20.0303L11.0303 15.0303L9.96967 13.9697L4.96967 18.9697L6.03033 20.0303ZM15.767 6.25H19.5V4.75H15.767V6.25ZM18.75 5.5V9.233H20.25V5.5H18.75ZM18.9697 4.96967L13.9697 9.96967L15.0303 11.0303L20.0303 6.03033L18.9697 4.96967Z',
                  onclick: () => {
                    setGraphModalVisible(true);
                    setTimeout(() => {
                      setGraphModalKey(new Date().valueOf());
                    }, 100);
                  },
                },
                myDownload: {
                  show: true,
                  title: 'Download Data',
                  icon: 'path://M12.5535 16.5061C12.4114 16.6615 12.2106 16.75 12 16.75C11.7894 16.75 11.5886 16.6615 11.4465 16.5061L7.44648 12.1311C7.16698 11.8254 7.18822 11.351 7.49392 11.0715C7.79963 10.792 8.27402 10.8132 8.55352 11.1189L11.25 14.0682V3C11.25 2.58579 11.5858 2.25 12 2.25C12.4142 2.25 12.75 2.58579 12.75 3V14.0682L15.4465 11.1189C15.726 10.8132 16.2004 10.792 16.5061 11.0715C16.8118 11.351 16.833 11.8254 16.5535 12.1311L12.5535 16.5061Z,M3.75 15C3.75 14.5858 3.41422 14.25 3 14.25C2.58579 14.25 2.25 14.5858 2.25 15V15.0549C2.24998 16.4225 2.24996 17.5248 2.36652 18.3918C2.48754 19.2919 2.74643 20.0497 3.34835 20.6516C3.95027 21.2536 4.70814 21.5125 5.60825 21.6335C6.47522 21.75 7.57754 21.75 8.94513 21.75H15.0549C16.4225 21.75 17.5248 21.75 18.3918 21.6335C19.2919 21.5125 20.0497 21.2536 20.6517 20.6516C21.2536 20.0497 21.5125 19.2919 21.6335 18.3918C21.75 17.5248 21.75 16.4225 21.75 15.0549V15C21.75 14.5858 21.4142 14.25 21 14.25C20.5858 14.25 20.25 14.5858 20.25 15C20.25 16.4354 20.2484 17.4365 20.1469 18.1919C20.0482 18.9257 19.8678 19.3142 19.591 19.591C19.3142 19.8678 18.9257 20.0482 18.1919 20.1469C17.4365 20.2484 16.4354 20.25 15 20.25H9C7.56459 20.25 6.56347 20.2484 5.80812 20.1469C5.07435 20.0482 4.68577 19.8678 4.40901 19.591C4.13225 19.3142 3.9518 18.9257 3.85315 18.1919C3.75159 17.4365 3.75 16.4354 3.75 15Z',
                  onclick: () => {
                    setDownloadModalVisible(true);
                    setDownloadModalKey(new Date().valueOf());
                  },
                },
              },
            },
            dataZoom: null,
            tooltip: null,
            series: [
              {
                ...option.series[0],
                data: chartData.prev,
                silent: true,
              },
              {
                ...option.series[1],
                data: chartData.baseSimplified,
                silent: true,
              },
              {
                ...option.series[2],
                data: chartData.next,
                silent: true,
              },
            ],
            animation: false,
          }}
        />
      </Observer>

      <Modal
        title={ioName}
        visible={graphModalVisible}
        onCancel={() => setGraphModalVisible(false)}
        width="80%"
        footer={null}
      >
        {graphModalKey && (
          <ReactECharts
            {...props}
            key={graphModalKey}
            opts={{
              useDirtyRect: true,
            }}
            style={{height: 400}}
            option={{
              ...option,
              series: [
                {
                  ...option.series[0],
                  data: chartData.prev,
                },
                {
                  ...option.series[1],
                  data: chartData.base,
                },
                {
                  ...option.series[2],
                  data: chartData.next,
                },
              ],
              toolbox: {
                feature: {
                  saveAsImage: {
                    show: true,
                    title: 'Save As Image',
                  },
                },
              },
            }}
          />
        )}
      </Modal>

      {downloadModalKey && (
        <StationDashboardGraphDownloadModal
          isSensor={true}
          key={downloadModalKey}
          zoneIoId={zoneIoId}
          visible={downloadModalVisible}
          onOk={() => setDownloadModalVisible(false)}
          onCancel={() => setDownloadModalVisible(false)}
          chartSelectedRange={selectedRange}
          chartSelectedDates={[beginAt, endAt]}
        />
      )}
    </Spin>
  );
};
